/**
 * Assessments dashboard section: list with create/edit collapse.
 */

import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CollapseReplaceWrapper } from '@/components/common/CollapseReplaceWrapper';
import { useDetailsView } from '@/hooks/useDetailsView';
import { AssessmentsDataTable } from '@/components/assessments/AssessmentsDataTable';
import { CreateAssessmentView } from '@/components/assessments/CreateAssessmentView';
import { EditAssessmentView } from '@/components/assessments/EditAssessmentView';
import { usePageActions } from '@/hooks/usePageActions';
import type { ElementWidth } from '@/types/interface-elements.types';

/** Props for {@link AssessmentsSection}. */
interface AssessmentsSectionProps {
  onSuccess?: () => void;
  layoutWidth?: ElementWidth;
}

/** Dashboard widget: assessments list. */
export const AssessmentsSection: React.FC<AssessmentsSectionProps> = ({
  onSuccess,
  layoutWidth = 'full',
}) => {
  const { canCreate } = usePageActions('assessments');
  const [panelMode, setPanelMode] = React.useState<'none' | 'create' | 'edit'>('none');
  const [editAssessmentId, setEditAssessmentId] = React.useState<string | null>(null);
  const queryClient = useQueryClient();
  const { openRowId, openRow, closeRow } = useDetailsView();

  const closePanel = React.useCallback(() => {
    setPanelMode('none');
    setEditAssessmentId(null);
  }, []);

  const handleSuccess = React.useCallback(async () => {
    closePanel();
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['assessments'], refetchType: 'all' }),
      queryClient.invalidateQueries({ queryKey: ['assessment'], refetchType: 'all' }),
    ]);
    onSuccess?.();
  }, [closePanel, queryClient, onSuccess]);

  /** Stable JSX node for the form — avoids unmounting {@link EditAssessmentView} on re-renders. */
  const formContent = React.useMemo<React.ReactNode>(() => {
    if (panelMode === 'create') {
      return <CreateAssessmentView onClose={closePanel} onSuccess={handleSuccess} />;
    }
    if (panelMode === 'edit' && editAssessmentId) {
      return (
        <EditAssessmentView
          key={editAssessmentId}
          assessmentId={editAssessmentId}
          onClose={handleSuccess}
        />
      );
    }
    return null;
  }, [panelMode, editAssessmentId, closePanel, handleSuccess]);

  return (
    <CollapseReplaceWrapper mode="replace" isOpen={panelMode !== 'none'} formContent={formContent}>
      <AssessmentsDataTable
        expandedRowId={openRowId}
        onToggleRow={openRow}
        onCloseDetails={closeRow}
        showCreateButton={canCreate}
        onCreateAssessment={() => {
          closeRow();
          setEditAssessmentId(null);
          setPanelMode('create');
        }}
        onEditAssessment={(assessmentId) => {
          closeRow();
          setEditAssessmentId(assessmentId);
          setPanelMode('edit');
        }}
        layoutWidth={layoutWidth}
        isSection={true}
      />
    </CollapseReplaceWrapper>
  );
};
